import axios from 'axios';
import { getAccessToken, getRefreshToken, getSessionIdentity, storeSessionTokens, expireSession, assertActiveSession } from './session';
export { TOKEN_KEY, REFRESH_TOKEN_KEY, storeSessionTokens, clearSessionTokens } from './session';

const CONFIGURED_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);
const BACKEND_URL_IS_LOOPBACK = (() => {
  if (!CONFIGURED_BACKEND_URL) return false;
  try {
    return LOOPBACK_HOSTS.has(new URL(CONFIGURED_BACKEND_URL, window.location.origin).hostname);
  } catch {
    return false;
  }
})();
// Never let a production visitor's browser send API requests to their own machine.
const BACKEND_URL = BACKEND_URL_IS_LOOPBACK && !LOOPBACK_HOSTS.has(window.location.hostname)
  ? ''
  : CONFIGURED_BACKEND_URL;
const FRONT_ONLY_MODE = String(process.env.REACT_APP_FRONT_ONLY || '').toLowerCase() === 'true';
export const IS_FRONT_ONLY = FRONT_ONLY_MODE;
export const API_BASE = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';
export const WS_BASE = BACKEND_URL
  ? `${BACKEND_URL.replace(/^http/, 'ws').replace(/\/$/, '')}/api/ws`
  : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/ws`;

const api = axios.create({ baseURL: API_BASE });

export const getApiErrorMessage = (error, fallback = 'Erreur') => {
  if (error?.code === 'API_UNAVAILABLE') return 'API indisponible';

  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;

  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === 'string' && first.trim()) return first;
    if (first && typeof first.msg === 'string') {
      const field = Array.isArray(first.loc)
        ? first.loc.filter((part) => part !== 'body').join('.')
        : '';
      return field ? `${field}: ${first.msg}` : first.msg;
    }
  }

  if (detail && typeof detail === 'object' && typeof detail.msg === 'string') {
    return detail.msg;
  }

  if (typeof error?.message === 'string' && error.message.trim() && error.message !== 'Network Error') {
    return error.message;
  }

  return fallback;
};

const isHtmlApiFallback = (response) => {
  if (response?.config?.expectsHtml) return false;
  const contentType = String(response?.headers?.['content-type'] || '').toLowerCase();
  const requestUrl = String(response?.config?.url || '');
  return requestUrl.startsWith('/') && contentType.includes('text/html');
};

api.interceptors.request.use((config) => {
  if (config.url !== '/auth/logout') assertActiveSession();
  const token = getAccessToken();
  config._sessionToken = token;
  config._sessionIdentity = getSessionIdentity();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Endpoints where a 401 is a genuine auth failure, not an expired access token.
const NO_REFRESH_RETRY_PREFIXES = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/2fa/verify', '/auth/social', '/auth/logout'];
let refreshPromise = null;
let refreshPromiseToken = null;

const refreshAccessToken = async () => {
  assertActiveSession();
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');
  const res = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refreshToken });
  if (getRefreshToken() !== refreshToken) throw new Error('Session changed while refreshing');
  assertActiveSession();
  storeSessionTokens(res.data.access_token, res.data.refresh_token);
  return res.data.access_token;
};

api.interceptors.response.use(
  (r) => {
    const requestUrl = r?.config?.url || '';
    const isAuthentication = NO_REFRESH_RETRY_PREFIXES.some(prefix => requestUrl.startsWith(prefix));
    if (!isAuthentication && r?.config?._sessionIdentity && r.config._sessionIdentity !== getSessionIdentity()) {
      return Promise.reject(new Error('Session changed while the request was running'));
    }
    if (isHtmlApiFallback(r)) {
      const error = new Error('API unavailable: received HTML instead of JSON');
      error.code = 'API_UNAVAILABLE';
      return Promise.reject(error);
    }
    return r;
  },
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    const url = originalRequest?.url || '';
    const skipRefresh = NO_REFRESH_RETRY_PREFIXES.some((prefix) => url.startsWith(prefix));

    if (status === 401 && originalRequest && !originalRequest._retriedAfterRefresh && !skipRefresh) {
      if (originalRequest._sessionIdentity !== getSessionIdentity()) return Promise.reject(error);
      originalRequest._retriedAfterRefresh = true;
      try {
        if (!refreshPromise || refreshPromiseToken !== getRefreshToken()) {
          refreshPromiseToken = getRefreshToken();
          const pending = refreshAccessToken().finally(() => {
            if (refreshPromise === pending) { refreshPromise = null; refreshPromiseToken = null; }
          });
          refreshPromise = pending;
        }
        const newAccessToken = await refreshPromise;
        if (newAccessToken !== getAccessToken()) throw new Error('Session changed while refreshing');
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        if (originalRequest._sessionIdentity === getSessionIdentity()) expireSession();
      }
    }
    return Promise.reject(error);
  }
);

export default api;
